// Het haarlijnraster (Kerngegevens op kandidaat, contact, bedrijf en vacature,
// het formulier Nieuwe kandidaat, de kwalificatie): cellen op een verdeler-
// achtergrond met een gap van 1px. Elk vak zonder cel toont die achtergrond als
// grijs gat. Tot 17 september 2026 telde elk raster zijn cellen met de hand
// (een formule met % 2, een vulcel achter een voorwaarde, een commentaar met
// de telling) en elke nieuwe of voorwaardelijke cel brak die telling weer.
//
// Hier staat de enige sluiting: de DOM is de waarheid. Een cel die een
// component niet rendert bestaat niet en telt niet mee; een cel met een eigen
// gridColumn (volle breedte) sluit de rij ervoor af. Een ongepaarde halve cel
// spant de rest van zijn rij, zodat er nooit een leeg vak is. Geen vulcellen,
// geen telling in de aanroeper.
//
// hairlineSpans is puur (soorten in, spans uit) en wordt getest;
// closeHairlineGrid past het op een element toe. components/HomeV2/lib.js
// (HairlineGrid) roept die laatste aan na elke render en bij elke wijziging in
// de kinderen.

export const HAIRLINE_SPAN_ATTR = 'data-hairline-span';

/**
 * kinds: per kind van het raster 'half' of 'full', in DOM-volgorde.
 * cols: aantal kolommen.
 * Levert een Map index -> aantal kolommen dat die halve cel moet spannen. Alleen
 * de laatste halve cel van een niet-volle rij staat erin.
 */
export function hairlineSpans(kinds, cols = 2) {
  const out = new Map();
  const n = Math.max(1, Number(cols) || 1);
  let col = 0;
  let last = -1;
  const close = () => {
    if (col > 0 && last >= 0) out.set(last, n - col + 1);
    col = 0;
  };
  kinds.forEach((k, i) => {
    if (k === 'full') { close(); return; }
    col += 1;
    last = i;
    if (col === n) col = 0;
  });
  close();
  return out;
}

const kindOf = (el) => (el.style && el.style.gridColumn ? 'full' : 'half');

/**
 * Past hairlineSpans toe op de kinderen van `el`. Idempotent: eerdere spans van
 * deze functie (herkenbaar aan het data-attribuut plus dezelfde waarde) gaan
 * eerst weg, een gridColumn die React zelf zette blijft staan en telt als vol.
 * Kinderen met display none tellen niet mee.
 */
// cols 'auto': het aantal kolommen komt uit de gelegde track-lijst (auto-fit
// rasters zoals de kwalificatieband), dus na elke breedtewijziging opnieuw.
export function liveColumnCount(el) {
  if (typeof getComputedStyle !== 'function') return 1;
  const tpl = String(getComputedStyle(el).gridTemplateColumns || '').trim();
  // Een auto-fit raster meldt zijn ingeklapte lege tracks als 0px; die zijn geen
  // kolom. Zonder dit filter telde de kwalificatieband op een breed venster vijf
  // kolommen, kreeg de laatste cel span 4 en werd de eerste cel 240px smal.
  const live = tpl ? tpl.split(/\s+/).filter((t) => parseFloat(t) > 0).length : 0;
  return live || 1;
}

export function closeHairlineGrid(el, cols = 2) {
  if (!el || !el.children) return;
  const kids = Array.from(el.children).filter((k) => !(k.style && k.style.display === 'none'));
  for (const k of kids) {
    const s = k.getAttribute(HAIRLINE_SPAN_ATTR);
    if (s == null) continue;
    if (k.style.gridColumn === `span ${s}`) k.style.gridColumn = '';
    k.removeAttribute(HAIRLINE_SPAN_ATTR);
  }
  // Pas NA het wissen van de vorige spans meten: een eerder gezette span houdt
  // de lege tracks open en de telling zou zichzelf bevestigen.
  if (cols === 'auto') cols = liveColumnCount(el);
  const spans = hairlineSpans(kids.map(kindOf), cols);
  spans.forEach((span, i) => {
    kids[i].style.gridColumn = `span ${span}`;
    kids[i].setAttribute(HAIRLINE_SPAN_ATTR, String(span));
  });
}

// De haarlijn is de gap tussen de cellen. Chrome rondt elke celachtergrond per
// rand af op hele schermpixels; een gap van 1 CSS-pixel is bij een zoom onder
// 100% of een scherm met een gebroken pixelverhouding minder dan een
// schermpixel, en dan rondt de witte cel erboven en eronder naar dezelfde
// pixel en verdwijnt de lijn (eigenaarsmelding 5 okt 2026, eerder 17 sep met
// een celhoogte-fix in CityField die alleen op 100% hielp). Een gap van
// precies een geheel aantal schermpixels overleeft elke afronding: beide
// randen schuiven even ver. Afgerond naar boven op Chrome's layout-eenheid
// (1/64 px), zodat de gap nooit net onder een schermpixel valt.
export function hairlineGapPx(dpr) {
  const r = Number(dpr) > 0 ? Number(dpr) : 1;
  const device = Math.max(1, Math.round(r));
  return Math.ceil((device / r) * 64) / 64;
}
